import { PaperTooltip } from '@/components/PaperTooltip';
import { formattedPercent } from '@/utils';
import { Add, Clear, Paid, Remove } from '@mui/icons-material';
import { Box, Button, Card, Stack, Typography } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import React, { useEffect, useRef, useState } from 'react';
import { getCardPieCoverage } from '../utils';
import { SystemCardInputField } from './SystemCardInputField';
import { SystemCardPieChart } from './SystemCardPieChart';

type MuiIconComponent = typeof SvgIcon;

// these ratios come from the Figma design docs.
// most of these ratios are used by multiplying by the current height of the card
// (which is calculated using useEffect/useRef)
// to maintain a consistent card UI

const CARD_PADDING_PCT = '6%'; // 16 / 266;
const CARD_BORDER_RADIUS_RATIO = 12 / 266;
const TEXT_SIZE_RATIO = 12 / 266;

const HEADER_HEIGHT_RATIO = 20 / 266;

const SVG_AND_NAME_HEIGHT_RATIO = 110 / 266;
const SVG_AND_NAME_SPACING_RATIO = 10 / 266;
const SVG_HEIGHT_WIDTH_RATIO = 74 / 266;

const BUTTON_SELECTED_CONTAINER_HEIGHT_RATIO = 42 / 266;
const BUTTON_SELECTED_FONT_SIZE_RATIO = 26 / 266;
const BUTTON_SELECTED_CONTAINER_SPACING_RATIO = 11 / 266;
const BUTTON_SELECTED_WIDTH_RATIO = 40 / 266;
const BUTTON_BORDER_RADIUS_RATIO = 4 / 266;

const BUTTON_NOT_SELECTED_WIDTH_RATIO = (77 / 198) * (198 / 266);
const BUTTON_NOT_SELECTED_TEXT_SIZE_RATIO = 15 / 266;

const FOOTER_CONTAINER_HEIGHT_RATIO = 35 / 266;
const PIE_CHART_SIZE_RATIO = 28 / 266;

const REMOVE_BUTTON_SIZE_RATIO = 22 / 266;

type SystemCardProps = {
  count?: number;
  limit: number;
  hoverPopover: React.ReactElement;
  name: string;
  selected: boolean;
  systemImage: React.ReactElement;
  showLimit?: boolean;
  icons: {
    EnergyIcon?: MuiIconComponent;
    energyIconCount?: 1 | 2 | 3 | 4 | 5;
    costIconCount?: 1 | 2 | 3 | 4 | 5;
  };
  /**
   * When true the +/-/count controls are replaced by a read-only count badge,
   * while the remove button stays available. Used for technologies whose unit
   * count the backend discards (passive measures), so the UI does not accept
   * input that is thrown away. See `isPassiveTechnology` in ../utils.ts.
   *
   * The footer is left blank too: count ÷ limit means nothing for such a
   * measure, so the card shows neither the coverage pie nor `LIMIT n`.
   */
  unitsFixed?: boolean;
  actions: {
    onAdd?: () => void;
    onIncrement?: () => void;
    onDecrement?: () => void;
    onRemove?: () => void;
    onChangeCount?: (newCount: number) => void;
  };
  readOnly?: boolean;
};

export const SystemCard: React.FC<SystemCardProps> = React.memo(
  ({
    actions,
    count,
    icons,
    limit,
    hoverPopover,
    name,
    selected,
    systemImage,
    showLimit = true,
    readOnly,
    unitsFixed,
  }) => {
    const itemCount = count ? count : 0;
    const pieCoverage = getCardPieCoverage({
      count: itemCount,
      limit,
      selected,
    });
    const EnergyIcon = icons?.EnergyIcon;

    const cardRef = useRef<HTMLDivElement | null>(null);
    const [cardHeight, setCardHeight] = useState(0);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleTooltipOpen = () => setTooltipOpen(true);
    const handleTooltipClose = () => setTooltipOpen(false);

    const updateCardHeight = () => {
      if (cardRef.current) {
        setCardHeight(cardRef.current.offsetHeight);
      }
    };

    useEffect(() => {
      updateCardHeight();
      window.addEventListener('resize', updateCardHeight);
      return () => window.removeEventListener('resize', updateCardHeight);
    }, []);

    useEffect(() => {
      if (!tooltipOpen) return;
      window.addEventListener('scroll', handleTooltipClose, {
        capture: true,
        passive: true,
      });
      return () =>
        window.removeEventListener('scroll', handleTooltipClose, {
          capture: true,
        });
    }, [tooltipOpen]);

    return (
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        {selected && !readOnly && (
          <Box
            onClick={() => actions.onRemove?.()}
            sx={{
              position: 'absolute',
              top: `${cardHeight * 0.025 - 8}px`,
              left: `${cardHeight * 0.025 - 6}px`,
              width: `${cardHeight * REMOVE_BUTTON_SIZE_RATIO - 1}px`,
              height: `${cardHeight * REMOVE_BUTTON_SIZE_RATIO - 1}px`,
              bgcolor: 'secondary.main',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 2,
            }}
          >
            <Clear
              sx={{
                fontSize: `${cardHeight * REMOVE_BUTTON_SIZE_RATIO * 0.6}px`,
                color: 'primary.main',
                stroke: 'currentColor',
                strokeWidth: 3,
              }}
            />
          </Box>
        )}
        <Card
          elevation={8}
          ref={cardRef}
          sx={{
            backgroundColor: 'background.card',
            backgroundImage: 'url(/img/paper-texture.png)',
            opacity: 0.9,
            borderRadius: `${cardHeight * CARD_BORDER_RADIUS_RATIO}px`,
            p: CARD_PADDING_PCT,
            aspectRatio: '198 / 266', // as per figma files
            height: '100%',
            width: '100%',
          }}
        >
          <Stack
            justifyContent={'space-between'}
            width={'100%'}
            height={'100%'}
            spacing={0}
          >
            <PaperTooltip
              disableInteractive
              open={tooltipOpen}
              onOpen={handleTooltipOpen}
              onClose={handleTooltipClose}
              title={hoverPopover}
              placement={'right'}
              sx={{ width: '100%' }}
            >
              <Stack spacing={0}>
                {/** header */}
                <Stack
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  direction={'row'}
                  flex={`${HEADER_HEIGHT_RATIO} 1 0`}
                  minHeight={0}
                  sx={{
                    fontSize: `${cardHeight * HEADER_HEIGHT_RATIO}px`,
                    marginTop: '4px',
                  }}
                >
                  <Stack direction={'row'} spacing={0}>
                    {icons?.costIconCount &&
                      icons.costIconCount > 0 &&
                      Array.from({ length: icons.costIconCount }).map(
                        (_, idx) => (
                          <Paid key={idx} fontSize={'inherit'} color={'info'} />
                        ),
                      )}
                  </Stack>
                  <Stack direction={'row'} spacing={0}>
                    {EnergyIcon &&
                      icons?.energyIconCount &&
                      icons.energyIconCount > 0 &&
                      Array.from({ length: icons.energyIconCount }).map(
                        (_, idx) => (
                          <EnergyIcon
                            key={idx}
                            fontSize={'inherit'}
                            color={'info'}
                          />
                        ),
                      )}
                  </Stack>
                </Stack>
                {/** system image + name */}
                <Stack
                  direction={'column'}
                  flex={SVG_AND_NAME_HEIGHT_RATIO}
                  alignItems={'center'}
                  justifyContent={{ sm: 'flex-start', md: 'center' }}
                  sx={{ marginTop: '12px' }}
                >
                  <Stack
                    color={'secondary.main'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    spacing={`${SVG_AND_NAME_SPACING_RATIO}%`}
                  >
                    <Box
                      sx={{
                        // the width/height ratio of the SVG should be 74px / 266px
                        height: `${cardHeight * SVG_HEIGHT_WIDTH_RATIO}px`,
                        width: `${cardHeight * SVG_HEIGHT_WIDTH_RATIO}px`,
                      }}
                    >
                      {React.cloneElement(systemImage, {
                        height: '100%',
                        width: '100%',
                      })}
                    </Box>
                  </Stack>
                  <Typography
                    sx={{ width: '100%' }}
                    align={'center'}
                    textTransform={'uppercase'}
                    variant={'subtitle2'}
                    fontSize={`${cardHeight * TEXT_SIZE_RATIO}px`}
                    noWrap
                  >
                    {name}
                  </Typography>
                </Stack>
              </Stack>
            </PaperTooltip>

            {/** (add) or (remove, -, count, +) buttons */}
            <Stack
              alignItems={'center'}
              flex={BUTTON_SELECTED_CONTAINER_HEIGHT_RATIO}
            >
              {/** if item is 'in cart' and readOnly (or its unit count is
               * fixed, e.g. a passive measure), show count as static text */}
              {selected && (readOnly || unitsFixed) && (
                <Box
                  sx={{
                    width: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                    height: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                    borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
                    bgcolor: 'secondary.main',
                    color: 'info.dark',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 'inherit',
                  }}
                >
                  <Typography fontSize={'inherit'} fontWeight={'bold'}>
                    {itemCount}
                  </Typography>
                </Box>
              )}
              {/** if item is 'in cart', show: remove button, decrement button (if count > 1), item count field, increment button */}
              {selected && !readOnly && !unitsFixed && (
                <Stack
                  direction={'row'}
                  fontSize={`${cardHeight * BUTTON_SELECTED_FONT_SIZE_RATIO}px`}
                  spacing={`${cardHeight * BUTTON_SELECTED_CONTAINER_SPACING_RATIO}px`}
                >
                  <Button
                    variant={'contained'}
                    color={'info'}
                    size={'small'}
                    disabled={itemCount <= 1}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      width: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                      height: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                      borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
                      fontSize: 'inherit',
                      '&:hover': { backgroundColor: 'action.hover' },
                      opacity: itemCount <= 1 ? 0.6 : 1,
                    }}
                    onClick={() => actions.onDecrement?.()}
                  >
                    <Remove color={'secondary'} />
                  </Button>
                  <Box flex={1}>
                    <SystemCardInputField
                      onChangeCount={actions.onChangeCount}
                      cardHeight={cardHeight * BUTTON_SELECTED_WIDTH_RATIO}
                      borderRadius={cardHeight * BUTTON_BORDER_RADIUS_RATIO}
                      itemCount={itemCount}
                    />
                  </Box>
                  <Button
                    variant={'contained'}
                    color={count && count < limit ? 'info' : 'warning'}
                    onClick={() => actions.onIncrement?.()}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      width: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                      height: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                      fontSize: 'inherit',
                      '&:hover': { backgroundColor: 'action.hover' },
                      borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
                    }}
                  >
                    <Add fontSize={'inherit'} color={'secondary'} />
                  </Button>
                </Stack>
              )}
              {/** if item NOT 'in cart' show: add button */}
              {!selected && (
                <Button
                  color={'success'}
                  variant={'contained'}
                  onClick={() => actions.onAdd?.()}
                  sx={{
                    minWidth: 0,
                    borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
                    width: `${cardHeight * BUTTON_NOT_SELECTED_WIDTH_RATIO}px`, // 77px / 266 px
                    '&:hover': {
                      backgroundColor: 'info.main',
                      color: 'success.main',
                    },
                  }}
                >
                  <Typography
                    fontSize={`${cardHeight * BUTTON_NOT_SELECTED_TEXT_SIZE_RATIO}px`}
                    variant={'subtitle2'}
                  >
                    ADD
                  </Typography>
                </Button>
              )}
            </Stack>

            {/** footer: kept as an empty row when units are fixed, so the
             * card's proportions match its neighbours' */}
            {showLimit && (
              <Stack
                justifyContent={'space-between'}
                direction={'row'}
                alignItems={'center'}
                flex={FOOTER_CONTAINER_HEIGHT_RATIO}
              >
                {!unitsFixed && (
                  <>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      spacing={0.5}
                    >
                      <SystemCardPieChart
                        width={cardHeight * PIE_CHART_SIZE_RATIO}
                        coverage={pieCoverage}
                      />
                      {pieCoverage > 1 && (
                        <Typography
                          variant={'subtitle2'}
                          fontSize={`${cardHeight * TEXT_SIZE_RATIO}px`}
                        >
                          {formattedPercent(pieCoverage)}
                        </Typography>
                      )}
                    </Stack>
                    <Typography
                      variant={'subtitle2'}
                      fontSize={`${cardHeight * TEXT_SIZE_RATIO}px`}
                    >
                      LIMIT {limit}
                    </Typography>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </Card>
      </Box>
    );
  },
);

SystemCard.displayName = 'SystemCard';
